/**
 * ForecastPanel — Sprint 6 · #21 Pipeline-voorspelling op de Sales Pipeline.
 * Heuristisch model (heuristic-v1): win-kans per open lead, gekalibreerd op de
 * won/lost-historiek van de workspace. Toont verwachte wins + top-kansen met
 * uitleg-factoren. Data komt als prop van de Pipeline-pagina (één fetch,
 * gedeeld met de win-kans-chips op de lead-kaarten).
 */
import React, { useState } from "react";
import i18n from "i18next";
import { TrendingUp, ChevronDown, ChevronUp } from "lucide-react";
import { fmtEur } from "@/components/sales/pipeline/constants";

const STAGE_COLORS = { new: "#64748b", contacted: "#38bdf8", engaged: "#14e0c8", meeting: "#22c55e" };

export function probColor(p) {
    if (p >= 0.5) return "#22c55e";
    if (p >= 0.25) return "#eab308";
    return "var(--jr-text-3)";
}

export default function ForecastPanel({ data }) {
    const [open, setOpen] = useState(false);

    if (!data || !data.summary?.open_count) return null;
    const s = data.summary;

    return (
        <div className="mb-4 rounded-xl border border-white/10 bg-[var(--jr-surface)]" data-testid="forecast-panel">
            <button onClick={() => setOpen(!open)} data-testid="forecast-toggle"
                className="w-full flex items-center gap-3 px-4 py-3 text-left">
                <TrendingUp size={15} className="text-[#22c55e]" />
                <span className="text-sm font-medium text-white">{i18n.t("forecast.title")}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-[#22c55e]/40 text-[#22c55e] font-mono" data-testid="forecast-chip">
                    ~{s.expected_wins} {i18n.t("forecast.wins")} / {s.open_count} {i18n.t("forecast.open")}
                </span>
                {(s.pipeline_value || 0) > 0 && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full border border-[var(--jr-amber)]/40 text-[var(--jr-amber)] font-mono"
                        title={i18n.t("forecast.value_tip")}
                        data-testid="forecast-value-chip">
                        {fmtEur(s.weighted_value)} {i18n.t("forecast.weighted")} / {fmtEur(s.pipeline_value)}
                    </span>
                )}
                <span className="text-[10px] text-[var(--jr-text-3)] hidden sm:inline">
                    {data.calibrated
                        ? i18n.t("forecast.calibrated", { won: data.calibration.won, lost: data.calibration.lost })
                        : i18n.t("forecast.uncalibrated")}
                </span>
                <span className="ml-auto text-[var(--jr-text-3)]">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
            </button>
            {open && (
                <div className="px-4 pb-4">
                    {/* stage breakdown */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {Object.entries(s.by_stage).map(([st, v]) => (
                            <div key={st} className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)] px-3 py-2" data-testid={`forecast-stage-${st}`}>
                                <div className="text-[9px] font-mono uppercase tracking-[0.16em]" style={{ color: STAGE_COLORS[st] }}>
                                    {i18n.t(`pipeline_sales.stage_${st}`)}
                                </div>
                                <div className="mt-0.5 text-sm text-white font-mono">
                                    {v.count} <span className="text-[10px] text-[var(--jr-text-3)]">→ ~{v.expected}</span>
                                </div>
                                {(v.value || 0) > 0 && (
                                    <div className="text-[10px] font-mono text-[var(--jr-amber)]" data-testid={`forecast-stage-value-${st}`}>
                                        {fmtEur(v.weighted_value)} / {fmtEur(v.value)}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    {/* top chances */}
                    <div className="mt-3">
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">{i18n.t("forecast.top_title")}</div>
                        <div className="space-y-1.5">
                            {(data.top || []).map((l, i) => (
                                <div key={l.id} className="flex items-center gap-2 text-xs" data-testid={`forecast-top-${i}`}>
                                    <span className="font-mono text-[11px] w-11 text-right" style={{ color: probColor(l.probability) }}>
                                        {Math.round(l.probability * 100)}%
                                    </span>
                                    <span className="text-white truncate">{l.company_name}</span>
                                    {l.contact_name && <span className="text-[var(--jr-text-3)] truncate hidden sm:inline">· {l.contact_name}</span>}
                                    <span className="ml-auto flex items-center gap-1 flex-wrap justify-end">
                                        {l.factors.filter((f) => !f.startsWith("stage_")).slice(0, 3).map((f) => (
                                            <span key={f} className="text-[9px] font-mono px-1.5 py-0.5 rounded-full border border-white/10 text-[var(--jr-text-3)]">
                                                {i18n.t(`forecast.factor_${f}`)}
                                            </span>
                                        ))}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <p className="mt-3 text-[10px] text-[var(--jr-text-3)]">{i18n.t("forecast.model_note", { model: data.model })}</p>
                </div>
            )}
        </div>
    );
}
